// The prose of the season chronicle (chronicle.md §3, §4, R1166).
//
// The game server records facts, never text. This module is the one place
// that turns the facts of one page into the lines a person reads. It is a
// pure function of the page, the scope and the words, so `render.test.ts`
// reads it with no DOM and no network.
//
// The rules of a day page (chronicle.md §3.2, R1174):
//
//   1. A fact of a kind this client does not know is dropped. A server that
//      is newer than the client must not break the book.
//   2. Facts of one kind with the same params fold into one line. The line
//      counts the repeats in `times` (the sum of the `times` params, 1 for
//      a fact without one).
//   3. The pick: every moment first, then the other lines in topic order,
//      then by the world second `at`. At most CHRONICLE_DAY_LINES_MAX lines
//      stay.
//   4. The page shows the picked lines in topic order, then by `at`, so a
//      moment stands among the lines of its own topic.
//   5. A day with no line shows one quiet line. The closing line gives the
//      change of rank and the score.
//
// The variant pick is `hash(account, world, season, day, line index)`
// (chronicle.md §4.1). A German list has the length of the English list,
// so one index gives the same variant in both languages. The captain
// remark takes the same hash with a salt of its own. Where the fact names
// the captain by `cid` and the owner wrote voice lines for that captain in
// the Captain Designer, the remark comes from those lines (§4.3, R1240).

import {
  CHRONICLE_DAY_LINES_MAX, CHRONICLE_KINDS, CHRONICLE_TOPIC_ORDER,
  CHRONICLE_VOICE_POOLS,
  type ChronicleFact, type ChronicleGroup, type ChronicleKind,
  type ChronicleKindSpec, type ChronicleParamType,
} from '../../../gen/chronicle';
import { FACTIONS, RESOURCES } from '../../../gen/balance';
import { LOCALE, type Chronicle } from '../../../gen/strings';
import { groupThousands } from '../../../locale/group';
import { fnv1a } from '../../../render/fnv';

/** What a slot writes when its param is missing or cannot be read. */
export const MISSING = '—';

/** The kind table by id. */
const SPECS: ReadonlyMap<string, ChronicleKindSpec> =
  new Map(CHRONICLE_KINDS.map((k) => [k.id, k]));

/** Who reads the volume and which volume it is: the seed of every pick. */
export interface ChronicleScope {
  /** The account id. The public read passes the public id. */
  account: string;
  world: number;
  season: number;
  /** The world name the meta server sends with the volume. */
  worldName: string;
}

/** The part of a stored page the render reads. */
export interface ChroniclePageIn {
  day: number;
  rank: number;
  score: number;
  rankDelta: number;
  facts: readonly ChronicleFact[];
}

/** One line of a page. */
export interface ChronicleLine {
  text: string;
  /** The kind, or `quiet` and `close` for the two lines no fact wrote. */
  kind: ChronicleKind | 'quiet' | 'close';
  moment: boolean;
  group?: ChronicleGroup;
  /** The first fact the line came from. */
  fact?: ChronicleFact;
}

/** One day page, ready to draw. */
export interface ChronicleDayView {
  day: number;
  header: string;
  lines: ChronicleLine[];
  close: ChronicleLine;
}

/** A noun lookup: the English catalog noun to the noun of the language. */
export type NounLookup = (english: string) => string;

/** The default lookup keeps the English catalog noun. The book passes
 *  `chronicleNoun` of gen/nouns.ts, the table of the picked language (R1176). */
const englishNoun: NounLookup = (n) => n;

/**
 * The voice lines the owner wrote for one captain, by captain game id, or
 * undefined where the owner wrote none (chronicle.md §4.3, R1240). The book
 * of the owner passes the lookup of its WARDROOM; the public reader passes
 * none, because the lines show to the owner only.
 */
export type VoiceLookup = (cid: number) => readonly string[] | undefined;

/** The lookup that knows no captain: every remark takes its default pool. */
const noVoices: VoiceLookup = () => undefined;

/** The lookup over one roster: a captain with no line is left out. */
export function voiceLookup(
  captains: readonly { id: number; voice: readonly string[] }[],
): VoiceLookup {
  const byId = new Map<number, readonly string[]>();
  for (const c of captains) {
    const lines = c.voice.filter((l) => l.trim() !== '');
    if (c.id > 0 && lines.length > 0) byId.set(c.id, lines);
  }
  return (cid) => byId.get(cid);
}

/** FNV-1a over the parts of one seed. */
export function chronicleHash(...parts: (string | number)[]): number {
  return fnv1a(parts.join('|'));
}

/** The variant of `list` the seed picks, or '' for an empty list. */
function pickVariant(list: readonly string[] | undefined, seed: number): string {
  if (!list || list.length === 0) return '';
  return list[seed % list.length];
}

/** One list of the words by its key. */
function listOf(words: Chronicle, key: string): readonly string[] | undefined {
  const v = (words as unknown as Record<string, unknown>)[key];
  return Array.isArray(v) ? v as readonly string[] : undefined;
}

/** Fills every `{slot}` of a template. A slot with no value writes MISSING. */
export function fill(template: string, values: Readonly<Record<string, string>>): string {
  return template.replace(/\{(\w+)\}/g, (_, k: string) => values[k] ?? MISSING);
}

function upper(s: string): string {
  return s.toLocaleUpperCase(LOCALE);
}

/** One param value, written by its declared type (chronicle.md §5). */
function formatParam(
  type: ChronicleParamType, raw: string | undefined, noun: NounLookup = englishNoun,
): string {
  if (raw === undefined || raw === '') return MISSING;
  switch (type) {
    case 'int': {
      const n = Number(raw);
      return Number.isInteger(n) ? groupThousands(n) : MISSING;
    }
    case 'name':
      return raw;
    case 'noun':
      return upper(noun(raw));
    case 'faction': {
      const f = FACTIONS.find((x) => String(x.id) === raw);
      return f ? upper(f.name) : MISSING;
    }
    case 'resource': {
      const r = RESOURCES.find((x) => x.key === raw);
      return r ? upper(r.name) : MISSING;
    }
    case 'attr':
      // An attribute picks the remark pool and writes no word.
      return '';
  }
}

/** A fact's `times`, 1 where it carries none. */
function timesOf(f: ChronicleFact): number {
  const n = Number(f.params?.times);
  return Number.isInteger(n) && n > 0 ? n : 1;
}

/** The fold key: the kind and every param but `times`. */
function foldKey(f: ChronicleFact): string {
  const p = f.params ?? {};
  const keys = Object.keys(p).filter((k) => k !== 'times').sort();
  return `${f.kind}\u0000${keys.map((k) => `${k}=${p[k]}`).join('\u0000')}`;
}

/** One folded line before its words: the facts it came from and the count. */
export interface Folded {
  fact: ChronicleFact;
  spec: ChronicleKindSpec;
  times: number;
  /** The position of the first fact in the page, the last tie-breaker. */
  order: number;
}

/** The topic rank of a group. The season group sorts after every topic. */
function topicRank(g: ChronicleGroup): number {
  const i = CHRONICLE_TOPIC_ORDER.indexOf(g);
  return i < 0 ? CHRONICLE_TOPIC_ORDER.length : i;
}

/** Rules 1 and 2: drop the unknown kinds and fold the repeats. */
function foldFacts(facts: readonly ChronicleFact[]): Folded[] {
  const out: Folded[] = [];
  const byKey = new Map<string, Folded>();
  facts.forEach((f, order) => {
    const spec = SPECS.get(f.kind);
    if (!spec) return;
    const key = foldKey(f);
    const seen = byKey.get(key);
    if (seen) {
      seen.times += timesOf(f);
      return;
    }
    const row: Folded = { fact: f, spec, times: timesOf(f), order };
    byKey.set(key, row);
    out.push(row);
  });
  return out;
}

function byTopic(a: Folded, b: Folded): number {
  return topicRank(a.spec.group) - topicRank(b.spec.group)
    || (a.fact.at ?? 0) - (b.fact.at ?? 0)
    || a.order - b.order;
}

/** Rules 3 and 4: pick at most `max` lines, and order them by topic. */
export function pickLines(facts: readonly ChronicleFact[], max = CHRONICLE_DAY_LINES_MAX): Folded[] {
  const folded = foldFacts(facts);
  const picked = [...folded]
    .sort((a, b) => Number(b.spec.moment) - Number(a.spec.moment) || byTopic(a, b))
    .slice(0, Math.max(0, max));
  return picked.sort(byTopic);
}

/** The words of one folded fact at one line index. */
function lineText(
  row: Folded, index: number, day: number, scope: ChronicleScope,
  words: Chronicle, noun: NounLookup, voices: VoiceLookup,
): string {
  const seed = chronicleHash(scope.account, scope.world, scope.season, day, index);
  const template = pickVariant(listOf(words, `kind_${row.spec.id}`), seed);
  const params = row.fact.params ?? {};
  const values: Record<string, string> = {};
  for (const p of row.spec.params) {
    if (p.type === 'attr') continue;
    // A siege on a neutral object names faction 0: the garrison (R1179).
    values[p.name] = p.type === 'faction' && params[p.name] === '0'
      ? words.neutral_foe
      : formatParam(p.type, params[p.name], noun);
  }
  let text = fill(template, values);
  if (row.times > 1) text += ` ${words.times_suffix({ times: groupThousands(row.times) })}`;
  // The captain remark (chronicle.md §4.3): the kind carries a `voice`
  // param, the fact names the captain, and the attribute names a pool. The
  // owner's own lines for the captain `cid` take the place of the pool.
  const voice = Number(params.voice);
  const captain = params.captain;
  if (row.spec.params.some((p) => p.name === 'voice')
    && captain !== undefined && captain !== ''
    && Number.isInteger(voice) && voice >= 0 && voice < CHRONICLE_VOICE_POOLS.length) {
    const cid = Number(params.cid);
    const own = params.cid !== undefined && Number.isInteger(cid) && cid > 0 ? voices(cid) : undefined;
    const pool = own !== undefined && own.length > 0
      ? own
      : listOf(words, `voice_${CHRONICLE_VOICE_POOLS[voice]}`);
    const remark = pickVariant(pool, chronicleHash(scope.account, scope.world, scope.season, day, index, 'voice'));
    if (remark !== '') text += ` ${words.voice_remark({ captain, remark })}`;
  }
  return text;
}

/** The closing line: the change of rank and the score (chronicle.md §3.2). */
function closeLine(
  page: ChroniclePageIn, index: number, scope: ChronicleScope, words: Chronicle,
): ChronicleLine {
  const list = page.rankDelta > 0 ? words.close_up
    : page.rankDelta < 0 ? words.close_down : words.close_level;
  const seed = chronicleHash(scope.account, scope.world, scope.season, page.day, index);
  const text = fill(pickVariant(list, seed), {
    places: groupThousands(Math.abs(page.rankDelta)),
    rank: groupThousands(page.rank),
    score: groupThousands(page.score),
  });
  return { text, kind: 'close', moment: false };
}

/** One day page, written in the words of the picked language. */
export function renderDay(
  page: ChroniclePageIn, scope: ChronicleScope, words: Chronicle,
  noun: NounLookup = englishNoun, voices: VoiceLookup = noVoices,
): ChronicleDayView {
  const lines: ChronicleLine[] = pickLines(page.facts).map((row, i) => ({
    text: lineText(row, i, page.day, scope, words, noun, voices),
    kind: row.spec.id,
    moment: row.spec.moment,
    group: row.spec.group,
    fact: row.fact,
  }));
  if (lines.length === 0) {
    const seed = chronicleHash(scope.account, scope.world, scope.season, page.day, 0);
    lines.push({ text: pickVariant(words.quiet, seed), kind: 'quiet', moment: false });
  }
  return {
    day: page.day,
    header: words.day_header({ day: page.day, season: scope.season, world: scope.worldName }),
    lines,
    close: closeLine(page, lines.length, scope, words),
  };
}

/** The sections of the season page, in the order the page shows them. */
export type SeasonSection = 'summary' | 'captains' | 'tech' | 'ledger' | 'milestones';

/** Which section each season kind stands in. */
const SEASON_SECTION: Readonly<Partial<Record<ChronicleKind, SeasonSection>>> = {
  season_rank: 'summary',
  season_score: 'summary',
  season_honour: 'summary',
  season_title: 'summary',
  season_captains: 'captains',
  season_tech: 'tech',
  season_most_mined: 'ledger',
  season_most_spent: 'ledger',
  season_most_lost: 'ledger',
  milestone_first_ship: 'milestones',
  milestone_first_tech: 'milestones',
  milestone_first_siege: 'milestones',
  milestone_first_kill: 'milestones',
  milestone_first_loss: 'milestones',
};

/** The section order of the season page. */
const SEASON_SECTIONS: readonly SeasonSection[] =
  ['summary', 'captains', 'tech', 'ledger', 'milestones'];

/** One moment of the volume, for the MOMENTS strip. */
export interface ChronicleMoment { day: number; text: string }

/** The season page, ready to draw. */
export interface ChronicleSeasonView {
  header: string;
  sections: { id: SeasonSection; lines: ChronicleLine[] }[];
  moments: ChronicleMoment[];
}

/**
 * The season page (chronicle.md §3.1). Every season fact is one line, in
 * the order of its section; a section with no line is left out. A fact of
 * a day kind on the season page is dropped: the day pages carry it.
 * `days` are the day views of the volume, and their moments fill the
 * MOMENTS strip in day order.
 */
export function renderSeason(
  page: ChroniclePageIn, days: readonly ChronicleDayView[], scope: ChronicleScope,
  words: Chronicle, noun: NounLookup = englishNoun, voices: VoiceLookup = noVoices,
): ChronicleSeasonView {
  const buckets = new Map<SeasonSection, Folded[]>();
  page.facts.forEach((fact, order) => {
    const spec = SPECS.get(fact.kind);
    const section = spec ? SEASON_SECTION[spec.id] : undefined;
    if (!spec || !section) return;
    const list = buckets.get(section) ?? [];
    list.push({ fact, spec, times: 1, order });
    buckets.set(section, list);
  });
  let index = 0;
  const sections: ChronicleSeasonView['sections'] = [];
  for (const id of SEASON_SECTIONS) {
    const rows = buckets.get(id);
    if (!rows) continue;
    sections.push({
      id,
      lines: rows.map((row) => ({
        text: lineText(row, index++, page.day, scope, words, noun, voices),
        kind: row.spec.id,
        moment: false,
        group: row.spec.group,
        fact: row.fact,
      })),
    });
  }
  const moments = [...days].sort((a, b) => a.day - b.day).flatMap((d) =>
    d.lines.filter((l) => l.moment).map((l) => ({ day: d.day, text: l.text })));
  return {
    header: words.season_header({ season: scope.season, world: scope.worldName }),
    sections,
    moments,
  };
}
